// What went wrong while a diagram was being loaded, and what the reader can
// do about it. Kept out of the viewer so it can be reasoned about (and
// tested) on its own, and so every message has exactly one home.
//
// Two rules the product owner asked for:
//   - a failure that may simply be bad timing is tried once more, quietly,
//     before anyone is told anything;
//   - what is then said names what happened, and the reader is never left
//     without a way on.
import { isDatabaseUnreachable, unreachableMessage } from "../../utils/databaseOffline";

// The server was restarting, a proxy was between workers, or the request
// never left the browser. A 503 that names an unreachable database is not one
// of these: that answer is the truth and repeating it changes nothing.
const TRANSIENT_STATUS = [502, 503, 504];

export const isTransient = (res) =>
  Boolean(res) &&
  res.success === false &&
  !isDatabaseUnreachable(res) &&
  (res.status === undefined || res.status === null || TRANSIENT_STATUS.includes(res.status));

// Runs a request and, where the answer looks like bad timing, waits a moment
// and asks once more. `stillWanted` lets the caller drop out when the reader
// has moved on - a retry for an object nobody is looking at helps nobody.
export const withOneRetry = async (request, { delay = 400, stillWanted = () => true } = {}) => {
  const first = await request();
  if (first?.success || !isTransient(first) || !stillWanted()) return first;
  await new Promise((resolve) => setTimeout(resolve, delay));
  if (!stillWanted()) return first;
  const second = await request();
  return second === undefined ? first : second;
};

// The reference the backend puts on every answer (`X-Request-Id`), so a
// screenshot of this card leads straight to its log lines.
const referenceOf = (res) => (typeof res?.reference === "string" && res.reference.trim() ? res.reference.trim() : null);

// One failure, in words. `subject` is what was being loaded - "object" for
// the object itself, the format's own name for one of its diagrams - so the
// wording never has a format written into it.
export const describeFailure = (res, { subject = "diagram" } = {}) => {
  const reference = referenceOf(res);
  const said = typeof res?.msg === "string" && res.msg.trim() ? res.msg.trim() : "";

  if (res?.status === 404) {
    return {
      kind: "notFound",
      title: "This object doesn't exist in this database",
      text: "It may have been removed, or it belongs to another database.",
      canRetry: false,
      reference,
    };
  }
  if (isDatabaseUnreachable(res)) {
    return { kind: "unreachable", title: "This database can't be reached", text: unreachableMessage(res), canRetry: true, reference };
  }
  if (res?.status === 403) {
    return {
      kind: "forbidden",
      title: "You don't have access to this",
      text: said || "Ask an administrator of your workspace to give you access to this database.",
      canRetry: false,
      reference,
    };
  }
  // Nothing came back at all, or the server was between restarts. It was
  // already tried twice by the time this is shown.
  if (!res?.status || TRANSIENT_STATUS.includes(res.status)) {
    return {
      kind: "offline",
      title: "We couldn't reach the server",
      text: "The connection didn't get through - it may have been restarting. Nothing is lost; try again.",
      canRetry: true,
      reference,
    };
  }
  return {
    kind: "error",
    title: `The ${subject} couldn't be loaded`,
    text: said || "Something went wrong on our side. Try again in a moment.",
    canRetry: true,
    reference,
  };
};
